import { useCallback, useEffect, useState } from "react";
import {
  SequenceService,
  type ApprovalOut,
  type LaunchSequenceOut,
  type LaunchSequencePatch,
  type TouchOverridePatch,
} from "@services/sequence/sequenceService";
import { LaunchService } from "@services/launches/LaunchService";
import type { LaunchAdStatus } from "@models/launchAd";

const service = new SequenceService();

/*
    One campaign's playbooks, as the server resolves them.

    Writes refetch rather than patching the local copy. A touch override can change more
    than the field that was sent — `is_customized` and `catalog_updated` are derived server
    side — and guessing those here is how a screen starts disagreeing with what will send.
*/
export function useCampaignSequences(launchId: number | null) {
  const [sequences, setSequences] = useState<LaunchSequenceOut[]>([]);
  const [approval, setApproval] = useState<ApprovalOut>({ state: "not_sent", touch_count: 0 });
  const [tokenValues, setTokenValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (options?: { keepError?: boolean }) => {
    if (!launchId) {
      setSequences([]);
      return;
    }
    setIsLoading(true);
    // A reload triggered BY a failed write must not erase the message explaining it --
    // which is what clearing unconditionally did, so the first version of this fix
    // reported nothing at all and the test caught it.
    if (!options?.keepError) setError(null);
    try {
      /*
          Approval travels with the sequences rather than on its own schedule. Every write
          path withdraws the client's approval server side (MPN-2207), so a footer refreshed
          separately would keep showing Approved after an edit that just invalidated it.
      */
      const [loaded, state] = await Promise.all([
        service.getLaunchSequences(launchId),
        service.getApproval(launchId),
      ]);
      setSequences(loaded);
      setApproval(state);
    } catch {
      setError("Could not load this campaign's sequences.");
    } finally {
      setIsLoading(false);
    }
  }, [launchId]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
      The real values behind this campaign's tokens, for the client preview. Loaded apart
      from the sequences because they are a property of the campaign and its brand, which no
      sequence edit changes.
  */
  useEffect(() => {
    if (!launchId) {
      setTokenValues({});
      return;
    }
    let cancelled = false;
    service
      .getTokenValues(launchId)
      .then((values) => {
        if (!cancelled) setTokenValues(values ?? {});
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [launchId]);

  /*
      Every write goes through here so a failure cannot pass silently.

      These used to reject into nothing: callers invoke them as `void overrideTouch(...)`,
      there is no global unhandled-rejection handler, and `load()` never ran on the failing
      path — so after a refused or failed save the screen kept showing the edit and the
      writer believed their copy was saved. The hook already surfaces load failures the same
      way; this gives writes the same honesty.

      The message names the action rather than the endpoint, because "Could not save the
      trigger" tells somebody what to try again and "PATCH 500" does not.
  */
  const write = useCallback(
    async (what: string, run: () => Promise<unknown>) => {
      setError(null);
      try {
        await run();
        await load();
        return true;
      } catch {
        setError(`Could not ${what}. Nothing was changed — try again.`);
        // Re-read regardless: the server may have applied part of it, and showing the
        // stored truth beats leaving an edit on screen that does not exist. Keeping the
        // message is the whole point -- the refresh is a consequence of the failure, not
        // a reason to forget it.
        await load({ keepError: true });
        return false;
      }
    },
    [load],
  );

  const configure = useCallback(
    async (sequenceId: number, payload: LaunchSequencePatch) => {
      if (!launchId) return false;
      return write("save this playbook's settings", () =>
        service.configureSequence(launchId, sequenceId, payload),
      );
    },
    [launchId, write],
  );

  const overrideTouch = useCallback(
    async (sequenceId: number, touchId: number, payload: TouchOverridePatch) => {
      if (!launchId) return false;
      return write("save this change", () => service.overrideTouch(launchId, sequenceId, touchId, payload));
    },
    [launchId, write],
  );

  const resetTouch = useCallback(
    async (sequenceId: number, touchId: number) => {
      if (!launchId) return false;
      return write("reset this touch", () => service.resetTouch(launchId, sequenceId, touchId));
    },
    [launchId, write],
  );

  /*
      Moving one touch's approval status by hand (MPN-2207). Straight at the launch-ad row,
      the same endpoint the ad card uses, rather than a sequence-shaped one: a touch IS a
      launch ad, and a second write path would let the two screens disagree about a value
      they both show.
  */
  const setTouchStatus = useCallback(
    async (launchAdId: number, status: LaunchAdStatus) => {
      return write("change this touch's status", () =>
        new LaunchService().updateLaunchAdStatus(launchAdId, status),
      );
    },
    [write],
  );

  const deleteAddedStep = useCallback(
    async (sequenceId: number, overrideId: number) => {
      if (!launchId) return false;
      return write("delete this step", () => service.deleteAddedStep(launchId, sequenceId, overrideId));
    },
    [launchId, write],
  );

  /*
      One request per playbook, which is the design's own wording: each goes to the client
      separately and lands in their My Todos as its own item. Sending them in sequence rather
      than at once keeps the order of the Ops history entries readable.
  */
  const sendForApproval = useCallback(
    async (sequenceIds: number[]) => {
      if (!launchId) return null;
      let last = null;
      for (const sequenceId of sequenceIds) {
        last = await service.sendPlaybookForApproval(launchId, sequenceId);
      }
      if (last) setApproval(last);
      await load();
      return last;
    },
    [launchId, load],
  );

  return {
    sequences,
    approval,
    tokenValues,
    sendForApproval,
    isLoading,
    error,
    reload: load,
    configure,
    overrideTouch,
    resetTouch,
    deleteAddedStep,
    setTouchStatus,
  };
}
